import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import Spinner from '/@/components/spinner';
import { useRecommendationsDashboard } from '/@/hooks/dashboards';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useCurrentCampaignYear } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import RecommendationList from '../../components/recommendations-list';
import { RecommendationsDashboardCards } from '../../components/recommendations-tab-cards';

const GrowerRecommendationsView = () => {
    const growerNodeId = useOwnUserNode();
    const currentCampaignYear = useCurrentCampaignYear();
    const form = useForm<{
        year: number;
    }>({
        defaultValues: {
            year: currentCampaignYear ?? new Date().getFullYear(),
        },
    });

    // Update form value when currentCampaignYear becomes available
    useEffect(() => {
        if (currentCampaignYear !== null) {
            form.setValue('year', currentCampaignYear);
        }
    }, [currentCampaignYear, form]);

    const { data: hybridRecommendations, isValidating: isValidatingHybrid } = useRecommendationsDashboard({
        year: form.watch('year'),
        node: growerNodeId.data?.id,
        type_in: ['corn', 'soy', 'density'],
    });

    const { data: cropProtectionRecommendations, isValidating: isValidatingCropProtection } =
        useRecommendationsDashboard({
            year: form.watch('year'),
            node: growerNodeId.data?.id,
            type_in: ['disease', 'plague', 'weed', 'seed_treatment'],
        });

    if (!growerNodeId.data?.id) {
        return (
            <Stack height='100%' width='100%' alignItems='center' justifyContent='center'>
                <Spinner />
            </Stack>
        );
    }

    return (
        <PageContainer
            header={
                <Header
                    title={_('Recomendaciones')}
                    subtitle={_(
                        'Consultá las recomendaciones generadas para los lotes de el productor durante la campaña.'
                    )}
                    actions={[
                        <MuiSelect
                            key='year'
                            {...form.register('year')}
                            onChange={(e) => form.setValue('year', Number(e.target.value))}
                            type='number'
                            value={form.watch('year')}
                            inputLabel={_('Campaña')}
                            options={Array.from({ length: 5 }, (_unused, index) => ({
                                label: `${new Date().getFullYear() - index} / ${new Date().getFullYear() - index + 1}`,
                                value: (new Date().getFullYear() - index).toString(),
                            }))}
                            size='small'
                            data-testid='grower-recommendations-year-select'
                            sx={{
                                width: {
                                    md: 200,
                                },
                            }}
                        />,
                    ]}
                />
            }
        >
            <Stack direction='column' spacing={3} py={4}>
                <RecommendationsDashboardCards
                    hybridRecommendations={hybridRecommendations}
                    cropProtectionRecommendations={cropProtectionRecommendations}
                    isValidating={isValidatingHybrid || isValidatingCropProtection}
                />

                <RecommendationList
                    userNodeId={growerNodeId.data?.id}
                    year={form.watch('year')}
                    nodeScope={getNodeScopeFilters('myFields')}
                />
            </Stack>
        </PageContainer>
    );
};

export default GrowerRecommendationsView;
